import { computed, effect, Injectable, signal } from '@angular/core';
import {
  DEFAULT_PRESET_ID,
  LAYOUT_PRESETS,
  LAYOUT_SCHEMA_VERSION,
  LAYOUT_STORAGE_KEY,
  LayoutPresetId,
  LayoutPreset,
  PaneId,
  PaneState,
} from '../../entity/layout-presets';

@Injectable({
  providedIn: 'root',
})
export class LayoutStateService {
  private readonly _layout = signal<LayoutPreset>(this.clonePreset(DEFAULT_PRESET_ID));

  readonly activePresetId = computed(() => this._layout().activePresetId);
  readonly taskBankSize = computed(() => this._layout().taskBankSize);

  constructor() {
    this.restore();
    effect(() => {
      this.persist(this._layout());
    });
  }

  /**
   * Returns a signal that exposes whether the requested pane is visible.
   * This keeps the layout truth in the service while allowing components to reactively read a specific pane state.
   *
   * @param id The pane identifier to inspect, such as routeList or map.
   * @returns A reactive signal that resolves to true when the pane is visible.
   */
  isPaneVisible(id: PaneId) {
    return computed(() => this.findPaneById(id)?.visible ?? false);
  }

  /**
   * Returns a signal with the requested pane width percentage.
   * Components consume this to render pane sizes without owning layout state themselves.
   *
   * @param id The pane identifier whose size should be read.
   * @returns A reactive signal exposing the pane size in percent form.
   */
  getPaneSize(id: PaneId) {
    return computed(() => this.findPaneById(id)?.size ?? 0);
  }

  /**
   * Switches the dashboard to a named preset and normalizes the result with invariants.
   * Route List visibility and the overall layout shape are always restored to a valid state.
   *
   * @param presetId The preset identifier to activate, such as routeMap or threePane.
   */
  selectPreset(presetId: LayoutPresetId): void {
    this._layout.set(this.clonePreset(presetId));
  }

  /**
   * Restores the layout from localStorage on service initialization.
   * Missing or malformed values fall back to the default preset instead of crashing the app.
   */
  private restore(): void {
    const presetId = this.readStorage() ?? DEFAULT_PRESET_ID;
    this._layout.set(this.clonePreset(presetId));
  }

  /**
  * Persists the selected preset whenever the layout signal changes.
  * Runtime splitter changes are deliberately excluded so reload restores the
  * canonical preset proportions.
   * Storage failures are intentionally swallowed so the UI remains available in private browsing or quota-limited scenarios.
   *
   * @param layout The current layout model whose selected preset is persisted.
   */
  private persist(layout: LayoutPreset): void {
    try {
      localStorage.setItem(LAYOUT_STORAGE_KEY, JSON.stringify({
        schemaVersion: LAYOUT_SCHEMA_VERSION,
        activePresetId: layout.activePresetId,
      }));
    } catch {
      // Ignore storage failures (quota/private mode) to keep UI responsive.
    }
  }

  /**
   * Reads the stored JSON payload and validates it against the current schema version.
   * Invalid or older payloads return null so the service can safely revert to default values.
   *
  * @returns The stored preset id, or null when nothing valid can be restored.
   */
  private readStorage(): LayoutPresetId | null {
    try {
      const raw = localStorage.getItem(LAYOUT_STORAGE_KEY);
      if (!raw) {
        return null;
      }
      return this.parseStoredPresetId(JSON.parse(raw));
    } catch {
      return null;
    }
  }

  /**
  * Validates a raw storage payload and extracts the selected preset id.
  * Unsupported versions or malformed values are rejected so the app always defaults safely.
   *
   * @param data The untrusted payload read from localStorage.
  * @returns A preset id when the payload is valid; otherwise null.
   */
  private parseStoredPresetId(data: unknown): LayoutPresetId | null {
    if (!data || typeof data !== 'object') {
      return null;
    }

    const candidate = data as { schemaVersion?: unknown; activePresetId?: unknown };

    if (candidate.schemaVersion !== LAYOUT_SCHEMA_VERSION) {
      return null;
    }

    if (!this.isLayoutPresetId(candidate.activePresetId)) {
      return null;
    }

    return candidate.activePresetId;
  }

  /**
   * Returns the current pane state for a specific pane id.
   * This helper keeps the computed accessors and invariant logic centralized in the service.
   *
   * @param id The pane identifier to look up.
   * @returns The matching pane state if it exists, otherwise undefined.
   */
  private findPaneById(id: PaneId): PaneState | undefined {
    return this._layout().topPanes.find((pane) => pane.id === id);
  }

  /**
   * Deep-clones the preset object to avoid mutating the shared catalog when a preset is selected or updated.
   *
   * @param id The preset identifier to clone from the preset catalog.
   * @returns A detached copy of the selected preset layout.
   */
  private clonePreset(id: LayoutPresetId): LayoutPreset {
    return structuredClone(LAYOUT_PRESETS[id]);
  }

  /**
   * Type guard that narrows a string to a known preset id.
   *
   * @param id A value to check against the known layout preset ids.
   * @returns True when the value is a supported preset id.
   */
  private isLayoutPresetId(id: unknown): id is LayoutPresetId {
    return typeof id === 'string' && Object.prototype.hasOwnProperty.call(LAYOUT_PRESETS, id);
  }

}

